import React, { useEffect } from 'react';

import PropTypes from 'prop-types';

import { identify, setBaseProperties } from '@adsk/acs-web-analytics';
import {
  UserProvider,
  SubscriptionsProvider,
  useOptionalUser,
} from '@adsk/alloy-provider';
import { LocaleProvider } from '@adsk/alloy-react-locale';
import { Reset } from '@adsk/alloy-react-theme';

import ConnectedTopNavigationOptionalUser from './ConnectedTopNavigationOptionalUser';
import TopNavigationLayout from './TopNavigationLayout';

const AnalyticsInitializer = () => {
  const { data: user, loading } = useOptionalUser();

  const notApplicable = 'N/A';

  useEffect(() => {
    user &&
      !loading &&
      identify(user.analyticsId, {
        ...user,
      });
  }, [loading, user]);

  useEffect(() => {
    setBaseProperties({
      license: notApplicable,
      tool_id: notApplicable,
      product_id: notApplicable,
      account_id: notApplicable,
      project_id: notApplicable,
    });
  }, []);

  return null;
};

/**
 * PublicPageLayout component is the root component to show a page view for both logged in and logged out users.
 * This component should be used for public links, where the user may or may not be logged in.
 * This component **would not redirect** the user to login page if the user is not logged in.
 */
const PublicPageLayout = React.forwardRef(
  ({ children, onLogout, locale, renderAboveLayout }, ref) => {
    return (
      <LocaleProvider locale={locale}>
        <Reset />
        <UserProvider redirectLoggedOutUsers={false}>
          <SubscriptionsProvider>
            <AnalyticsInitializer />
            <TopNavigationLayout
              ref={ref}
              renderTopNavigation={() => (
                <ConnectedTopNavigationOptionalUser onLogout={onLogout} />
              )}
              renderAboveLayout={renderAboveLayout}
            >
              {children}
            </TopNavigationLayout>
          </SubscriptionsProvider>
        </UserProvider>
      </LocaleProvider>
    );
  },
);

PublicPageLayout.displayName = 'PublicPageLayout';

PublicPageLayout.propTypes = {
  /** Page content */
  children: PropTypes.node,
  /** callback called on logout */
  onLogout: PropTypes.func,
  /** localization, default: en */
  locale: PropTypes.string,
  /** Items that will render above the layout*/
  renderAboveLayout: PropTypes.func,
};

export default PublicPageLayout;
